import React, { PureComponent } from 'react';

import { type AppName, ProductName } from '@atlassian/ptc-common';

import { prefixWithContextEntryPoint } from '../../utils/url';

import AppConfigContext from './AppConfigContext';
import { getTypeIcon } from './flag-utils';
import { type AppConfigProviderProps, type AppConfigProviderState, type Flag } from './types';

const appProductMap: Required<Record<AppName, ProductName | ''>> = {
	'': '',
	'site-directory': '',
	'confluence-directory': ProductName.CONFLUENCE,
	'jira-directory': ProductName.JIRA,
	'teamcentral-directory': ProductName.ATLAS,
	'home-directory': ProductName.HOME,
};

const mapAppToProduct = (app: AppName): ProductName | '' => {
	return appProductMap[app];
};

// `AppConfigProvider` will be rendered as a wrapper component in near top level, e.g. `AppContainer`
// Props and States of AppConfigProvider should be similar
// eslint-disable-next-line @repo/internal/react/no-class-components
export default class AppConfigProvider extends PureComponent<
	AppConfigProviderProps,
	Partial<AppConfigProviderState>
> {
	addFlag = (props: Flag) => {
		const { type, ...rest } = props;

		this.props.addFlag({
			...rest,
			id: rest.id ? `${rest.id}.${Date.now()}` : Date.now(),
			type,
			appearance: 'normal',
			icon: getTypeIcon(type),
		});
	};

	push = (path: string) => {
		const { contextEntryPoint } = this.props;
		const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);
		return this.props.push(finalPath);
	};

	replace = (path: string) => {
		const { contextEntryPoint } = this.props;
		const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);

		return this.props.replace(finalPath);
	};

	state = {
		addFlag: this.addFlag,
		push: this.push,
		replace: this.replace,
		productName: mapAppToProduct(this.props.appName),
		productFeatureFlags: this.props.productFeatureFlags,
		invitePeopleHandlers: this.props.invitePeopleHandlers,
	};

	render() {
		// This is here to let productFeatureFlags from getting updated via props
		const productFeatureFlags = {
			...this.state.productFeatureFlags,
			...this.props.productFeatureFlags,
		};
		return (
			<AppConfigContext.Provider value={{ ...this.props, ...this.state, productFeatureFlags }}>
				{this.props.children}
			</AppConfigContext.Provider>
		);
	}
}
